// src/components/management/TeamsTab.js
//
// Teams, on their own screen.
//
//   • Companies are tabs, not a dropdown — there are only ever a few.
//   • A Super Admin sets teams up: create, rename, delete, and who is on each.
//   • A Main Admin can set which dispatcher runs each driver on their own
//     team (or leave a driver with none). They cannot move people between
//     teams, add or remove anyone, or touch another team.
//
// They used to live three levels down — Settings → Company Management → expand
// a company → Teams — which suited them when a team was just a label. They now
// carry who dispatches whom, which drivers a company can see, and the move of a
// driver between companies. That is weekly operational work, not configuration,
// and it had no business being that hard to reach.
//
// One company at a time, because a team belongs to a company and dragging a
// driver between teams only makes sense inside one. The company tabs are how a
// Super Admin moves between the companies they manage.
//
// The writes live in useTeamActions, shared with the copy still nested inside
// Company Management, so the two screens cannot drift apart.

import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import TeamsPanel from '../settings/TeamsPanel';
import { useTeamActions, manageableCompanies } from './useTeamActions';
import { getCompanyTeams, getUserTeamIds } from '../../utils/teams';

const rolesOf = (user) => {
  if (!user) return [];
  if (Array.isArray(user.role)) return user.role.filter(Boolean);
  return user.role ? [user.role] : [];
};

export default function TeamsTab({ loggedInUser }) {
  const tenantId = loggedInUser?.tenantId;
  const isSuper = rolesOf(loggedInUser).includes('Super Admin');
  const isMainAdminViewer = !isSuper && rolesOf(loggedInUser).includes('Main Admin');
  const myTeamIds = getUserTeamIds(loggedInUser);

  // Only a Super Admin changes teams, same as in Company Management. Everyone
  // else who can see a company still gets the panel read-only — a Main Admin
  // asking "who is on my team and who dispatches them" should not have to ask
  // somebody else, and showing it cannot change anything.
  const canManage = isSuper;

  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [message, setMessage] = useState(null);

  // The hook is opened to a Main Admin too, for the dispatcher link only. The
  // other writes are handed to the panel solely when the viewer is a Super
  // Admin, and the link itself is checked against the Main Admin's own team.
  const { teamBusy, saveTeams, assignPersonToTeam, linkDriverToDispatcher } =
    useTeamActions({ loggedInUser, tenantId, users, drivers, companies, canManage: canManage || isMainAdminViewer, onError: setMessage });

  const canLinkInTeam = (teamId) =>
    !!teamId && (isSuper || (isMainAdminViewer && myTeamIds.includes(teamId)));

  const linkIfAllowed = async (driverId, dispatcherId) => {
    const driver = drivers.find(d => d.id === driverId);
    if (!driver || !canLinkInTeam(driver.teamId)) return;
    if (dispatcherId) {
      const dispatcher = users.find(u => u.id === dispatcherId);
      if (!dispatcher || !getUserTeamIds(dispatcher).includes(driver.teamId)) return;
    }
    await linkDriverToDispatcher(driverId, dispatcherId);
  };

  useEffect(() => {
    if (!tenantId) { setIsLoading(false); return undefined; }

    const unsubCompanies = onSnapshot(
      query(collection(db, 'companies'), where('tenantId', '==', tenantId)),
      snap => { setCompanies(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setIsLoading(false); },
      err => { console.error('Teams: companies', err); setIsLoading(false); }
    );

    const unsubUsers = onSnapshot(
      query(collection(db, 'users'), where('tenantId', '==', tenantId)),
      snap => setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => console.error('Teams: users', err)
    );

    // Deleted drivers are dropped here rather than in the query: the flag is
    // absent on older records, and a where() clause would skip those entirely.
    const unsubDrivers = onSnapshot(
      query(collection(db, 'drivers'), where('tenantId', '==', tenantId)),
      snap => setDrivers(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(d => !d.isDeleted && d.status !== 'Deleted')
      ),
      err => console.error('Teams: drivers', err)
    );

    return () => { unsubCompanies(); unsubUsers(); unsubDrivers(); };
  }, [tenantId]);

  const available = useMemo(
    () => manageableCompanies(companies, loggedInUser, isSuper),
    [companies, loggedInUser, isSuper]
  );

  // Land on a company rather than an empty screen, preferring one that already
  // has teams — that is the one somebody opening this page is looking for.
  useEffect(() => {
    if (selectedCompanyId || available.length === 0) return;
    const withTeams = available.find(c => getCompanyTeams(c).length > 0);
    setSelectedCompanyId((withTeams || available[0]).id);
  }, [available, selectedCompanyId]);

  const selected = available.find(c => c.id === selectedCompanyId) || null;

  if (isLoading) {
    return <div className="p-6 text-center text-gray-500">Loading teams…</div>;
  }

  if (available.length === 0) {
    return (
      <div className="p-6 bg-gray-50 border border-gray-200 rounded-md">
        <p className="text-sm text-gray-600">
          {isSuper
            ? <>No companies yet. Create one under <span className="font-medium">Companies</span> first — a team belongs to a company.</>
            : <>You are not assigned to a company, so there are no teams to show. A Super Admin assigns companies under Companies.</>}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Companies as tabs: there are only ever a handful, and a dropdown hid
          that there was more than one. */}
      <div className="flex flex-wrap items-end gap-1 border-b border-gray-200 mb-4">
        {available.map(c => {
          const n = getCompanyTeams(c).length;
          const active = c.id === selectedCompanyId;
          return (
            <button
              key={c.id}
              onClick={() => { setSelectedCompanyId(c.id); setMessage(null); }}
              className={
                'px-4 py-2 -mb-px text-sm rounded-t-md border ' +
                (active
                  ? 'bg-white border-gray-200 border-b-white font-semibold text-gray-900'
                  : 'bg-gray-50 border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100')
              }
            >
              {c.name}
              <span className={`ml-2 text-xs ${active ? 'text-gray-500' : 'text-gray-400'}`}>
                {n > 0 ? `${n} team${n === 1 ? '' : 's'}` : 'no teams'}
              </span>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-gray-500 mb-3">
        {canManage
          ? 'A team is a Main Admin, dispatchers and the drivers they run. Drag someone into a team, or click their name in the list under it. Click ✎ to rename a team.'
          : isMainAdminViewer
            ? 'You can choose which dispatcher runs each driver on your team. Moving people between teams is done by a Super Admin.'
            : 'A team is a Main Admin, dispatchers and the drivers they run. Only a Super Admin can change these.'}
      </p>

      {message && (
        <div className={`mb-3 px-3 py-2 rounded-md text-sm border ${
          message.type === 'error'
            ? 'bg-red-50 text-red-800 border-red-200'
            : 'bg-blue-50 text-blue-800 border-blue-200'
        }`}>
          {message.text}
        </div>
      )}

      {selected && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <TeamsPanel
            company={selected}
            users={users}
            drivers={drivers}
            canManage={canManage}
            busy={teamBusy}
            canLinkInTeam={canLinkInTeam}
            onSaveTeams={(nextTeams) => (isSuper ? saveTeams(selected, nextTeams) : undefined)}
            onAssignPerson={(payload) => (isSuper ? assignPersonToTeam(selected, payload) : undefined)}
            onLinkDispatcher={linkIfAllowed}
            // No onManageDrivers / onManageUsers: adding people TO a company
            // is company setup and lives on the Companies tab. Without them the
            // panel leaves those buttons out rather than showing dead ones.
          />
        </div>
      )}
    </div>
  );
}
